/**
 * ValidationErrorBadge - Visual indicator for validation errors on canvas nodes
 *
 * Shows validation error count with appropriate styling.
 * Positioned absolutely within the node container.
 */

import { memo, useCallback } from 'react';
import { makeStyles, tokens, Tooltip, mergeClasses } from '@fluentui/react-components';
import { ErrorCircle20Filled } from '@fluentui/react-icons';
import type { ValidationError } from '@/services/workflowValidationService';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  badge: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusCircular,
    border: `2px solid ${tokens.colorNeutralBackground1}`,
    cursor: 'pointer',
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorPaletteRedForeground1,
    ':hover': {
      transform: 'scale(1.1)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  // Position variants
  topRight: {
    top: '-6px',
    right: '-6px',
  },
  topLeft: {
    top: '-6px',
    left: '-6px',
  },
  bottomRight: {
    bottom: '-6px',
    right: '-6px',
  },
  bottomLeft: {
    bottom: '-6px',
    left: '-6px',
  },
  // Size variants
  small: {
    minWidth: '16px',
    height: '16px',
    fontSize: '10px',
    '& > svg': {
      fontSize: '12px',
    },
  },
  medium: {
    minWidth: '20px',
    height: '20px',
    fontSize: '11px',
    '& > svg': {
      fontSize: '14px',
    },
  },
  large: {
    minWidth: '24px',
    height: '24px',
    fontSize: '12px',
    '& > svg': {
      fontSize: '16px',
    },
  },
  // Count display (when more than 1 error)
  count: {
    paddingLeft: '4px',
    paddingRight: '4px',
    fontWeight: tokens.fontWeightSemibold,
  },
  // Icon-only mode (single error)
  iconOnly: {
    padding: 0,
  },
});

// =============================================================================
// TYPES
// =============================================================================

export type BadgePosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
export type BadgeSize = 'small' | 'medium' | 'large';

export interface ValidationErrorBadgeProps {
  /** Node name for accessibility */
  nodeName: string;
  /** Validation errors to display */
  errors: ValidationError[];
  /** Position of the badge relative to the node */
  position?: BadgePosition;
  /** Size of the badge */
  size?: BadgeSize;
  /** Click handler (e.g., to open validation panel) */
  onClick?: () => void;
  /** Additional CSS class */
  className?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Build tooltip content from validation errors
 */
function buildTooltipContent(errors: ValidationError[]): string {
  if (errors.length === 0) return '';

  const firstError = errors[0];
  if (errors.length === 1 && firstError) {
    return firstError.message;
  }

  // Show first 3 errors with count
  const displayed = errors.slice(0, 3);
  const messages = displayed.map((e) => `• ${e.message}`).join('\n');

  if (errors.length > 3) {
    return `${messages}\n...and ${errors.length - 3} more`;
  }

  return messages;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ValidationErrorBadge = memo(function ValidationErrorBadge({
  nodeName,
  errors,
  position = 'top-left',
  size = 'medium',
  onClick,
  className,
}: ValidationErrorBadgeProps) {
  const styles = useStyles();

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent node selection
      onClick?.();
    },
    [onClick]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        onClick?.();
      }
    },
    [onClick]
  );

  // Don't render if no errors
  if (!errors || errors.length === 0) {
    return null;
  }

  // Get position class
  const positionClass = {
    'top-right': styles.topRight,
    'top-left': styles.topLeft,
    'bottom-right': styles.bottomRight,
    'bottom-left': styles.bottomLeft,
  }[position];

  // Get size class
  const sizeClass = {
    small: styles.small,
    medium: styles.medium,
    large: styles.large,
  }[size];

  const tooltip = buildTooltipContent(errors);
  const ariaLabel = `${errors.length} validation error${errors.length !== 1 ? 's' : ''} on ${nodeName}. ${tooltip}`;
  const showCount = errors.length > 1;

  return (
    <Tooltip content={tooltip} relationship="description" positioning="above">
      <button
        type="button"
        className={mergeClasses(
          styles.badge,
          positionClass,
          sizeClass,
          showCount ? styles.count : styles.iconOnly,
          className
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        data-testid={`validation-badge-${nodeName}`}
        data-error-count={errors.length}
      >
        {showCount ? errors.length : <ErrorCircle20Filled />}
      </button>
    </Tooltip>
  );
});

ValidationErrorBadge.displayName = 'ValidationErrorBadge';
